import React, { useState, useEffect } from "react";

const SalaryCalculator = () => {
  const [grossSalary, setGrossSalary] = useState(100000);
  const [basicPercentage, setBasicPercentage] = useState(40);
  const [hraPercentage, setHraPercentage] = useState(50);
  const [pfContribution, setPfContribution] = useState(12);
  const [salaryType, setSalaryType] = useState("monthly");
  const [roundUp, setRoundUp] = useState(false);
  const [taxRegime, setTaxRegime] = useState("old");
  const [result, setResult] = useState({
    grossSalary: "0.00",
    basicSalary: "0.00",
    hra: "0.00",
    specialAllowance: "0.00",
    pfEmployee: "0.00",
    pfEmployer: "0.00",
    totalPf: "0.00",
    netSalary: "0.00",
    salaryTypeText: "",
    salaryTip: "",
  });

  // Salary Calculation Function
  const calculateSalary = () => {
    let gross = parseFloat(grossSalary);
    let basicPct = parseFloat(basicPercentage);
    let hraPct = parseFloat(hraPercentage);
    let pf = parseFloat(pfContribution);

    if (isNaN(gross) || gross < 0 || isNaN(basicPct) || basicPct < 0 || isNaN(hraPct) || hraPct < 0 || isNaN(pf) || pf < 0) return;

    // Calculate Basic Salary
    let basicSalary = (gross * basicPct) / 100;
    
    // Calculate HRA (% of Basic)
    let hra = (basicSalary * hraPct) / 100;
    
    // Calculate Special Allowance (remaining amount)
    let specialAllowance = gross - basicSalary - hra;
    
    // Calculate PF
    let pfEmployee = (basicSalary * pf) / 100;
    let pfEmployer = (basicSalary * pf) / 100;
    let totalPf = pfEmployee + pfEmployer;
    
    // Round up if selected
    if (roundUp) {
      basicSalary = Math.ceil(basicSalary);
      hra = Math.ceil(hra);
      specialAllowance = Math.ceil(specialAllowance);
      pfEmployee = Math.ceil(pfEmployee);
      pfEmployer = Math.ceil(pfEmployer);
      totalPf = pfEmployee + pfEmployer;
    }
    
    // Calculate Net Salary (In-hand)
    const netSalary = gross - pfEmployee;

    let typeText = "";
    let salaryTip = "";

    switch(salaryType) {
      case "monthly":
        typeText = "Monthly Salary Breakup";
        salaryTip = "Monthly salary shows your regular income. Multiply by 12 for annual CTC calculation.";
        break;
      case "annual":
        typeText = "Annual CTC Breakup";
        salaryTip = "Annual CTC includes all components. Divide by 12 to get monthly in-hand salary.";
        break;
      case "gross":
        typeText = "Gross Salary Details";
        salaryTip = "Gross salary is before deductions. Net/in-hand salary is what you receive after deductions.";
        break;
      case "ctc":
        typeText = "Cost to Company (CTC)";
        salaryTip = "CTC includes your salary plus employer contributions like PF, gratuity, and benefits.";
        break;
      case "custom":
        typeText = "Custom Salary Structure";
        salaryTip = "Customize components based on your offer letter. Different companies have different structures.";
        break;
      default:
        typeText = "Salary Calculation";
        salaryTip = "Understanding salary breakup helps you plan finances and compare job offers effectively.";
    }

    setResult({
      grossSalary: gross.toFixed(2),
      basicSalary: basicSalary.toFixed(2),
      hra: hra.toFixed(2),
      specialAllowance: specialAllowance.toFixed(2),
      pfEmployee: pfEmployee.toFixed(2),
      pfEmployer: pfEmployer.toFixed(2),
      totalPf: totalPf.toFixed(2),
      netSalary: netSalary.toFixed(2),
      salaryTypeText: typeText,
      salaryTip,
    });
  };

  useEffect(() => {
  calculateSalary();
}, [grossSalary, basicPercentage, hraPercentage, pfContribution, roundUp, salaryType, taxRegime]);


  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <h1>Salary Breakup Calculator</h1>
          <p>
            Calculate detailed salary breakup with Basic, HRA, Special Allowance, PF, and in-hand salary
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Salary Details
              </h2>

              {/* Gross Salary */}
              <div className="input-group">
                <label>Gross Salary (Monthly)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={grossSalary}
                    onChange={(e) => setGrossSalary(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[25000, 50000, 75000, 100000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setGrossSalary(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Salary Type */}
              <div className="input-group">
                <label>Salary Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "monthly", label: "Monthly" },
                    { value: "annual", label: "Annual CTC" },
                    { value: "gross", label: "Gross" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setSalaryType(type.value)}
                      className={`quick-select-btn ${salaryType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "ctc", label: "CTC" },
                    { value: "custom", label: "Custom" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setSalaryType(type.value)}
                      className={`quick-select-btn ${salaryType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Basic Percentage */}
              <div className="input-group">
                <label>Basic Salary (% of Gross)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={basicPercentage}
                    onChange={(e) => setBasicPercentage(e.target.value)}
                    className="calc-input"
                    placeholder="40"
                    step="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[30, 40, 50, 60].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setBasicPercentage(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* HRA Percentage */}
              <div className="input-group">
                <label>HRA (% of Basic)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={hraPercentage}
                    onChange={(e) => setHraPercentage(e.target.value)}
                    className="calc-input"
                    placeholder="50"
                    step="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[40, 50, 60, 100].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setHraPercentage(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* PF Contribution */}
              <div className="input-group">
                <label>PF Contribution (% of Basic)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={pfContribution}
                    onChange={(e) => setPfContribution(e.target.value)}
                    className="calc-input"
                    placeholder="12"
                    step="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 12, 15, 18].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setPfContribution(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Round Up */}
              <div className="input-group">
                <label>Round Amounts?</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Yes (Round Up)" },
                    { value: false, label: "No (Exact)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setRoundUp(type.value)}
                      className={`quick-select-btn ${roundUp === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tax Regime */}
              <div className="input-group">
                <label>Tax Regime</label>
                <div className="quick-select-group">
                  {[
                    { value: "old", label: "Old Regime" },
                    { value: "new", label: "New Regime" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setTaxRegime(type.value)}
                      className={`quick-select-btn ${taxRegime === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateSalary} className="calculate-btn">
                Calculate Salary Breakup
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Salary Breakup
              </h2>

              <div className="result-card">
                {/* Net Salary - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Net Salary (In-Hand)</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.netSalary).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.salaryTypeText} - After PF deduction
                  </div>
                </div>

                {/* Basic Salary */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Basic Salary</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.basicSalary).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {basicPercentage}% of Gross Salary
                  </div>
                </div>

                {/* HRA */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">House Rent Allowance (HRA)</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.hra).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {hraPercentage}% of Basic Salary
                  </div>
                </div>

                {/* Special Allowance */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Special Allowance</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <circle cx="12" cy="8" r="7"></circle>
                        <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.specialAllowance).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Remaining allowances
                  </div>
                </div>

                {/* PF Employee */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">PF Contribution (Employee)</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.pfEmployee).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {pfContribution}% of Basic (Deducted)
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Salary Components</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Basic Salary</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.basicSalary) / parseFloat(result.grossSalary)) * 100).toFixed(2)}% (₹{Number(result.basicSalary).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.basicSalary) / parseFloat(result.grossSalary)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">HRA</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.hra) / parseFloat(result.grossSalary)) * 100).toFixed(2)}% (₹{Number(result.hra).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.hra) / parseFloat(result.grossSalary)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Special Allowance</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.specialAllowance) / parseFloat(result.grossSalary)) * 100).toFixed(2)}% (₹{Number(result.specialAllowance).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill purple"
                        style={{
                          width: `${(parseFloat(result.specialAllowance) / parseFloat(result.grossSalary)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* PF Breakdown */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Provident Fund (PF) Details</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Employee PF (Deduction)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.pfEmployee).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `50%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Employer PF (Company Contribution)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.pfEmployer).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `50%`,
                        }}
                      />
                    </div>
                  </div>
                  <div style={{marginTop: '0.75rem', padding: '0.75rem', background: '#f3f4f6', borderRadius: '6px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <span style={{fontSize: '0.875rem', fontWeight: '600', color: '#374151'}}>Total PF Monthly</span>
                      <span style={{fontSize: '1rem', fontWeight: '700', color: '#059669'}}>₹{Number(result.totalPf).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </div>

                {/* Salary Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Salary Insights</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.salaryTip} Always review your salary slip and understand each component.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Salary Breakup</h3>
              <p>
                Salary breakup is the detailed breakdown of your total compensation (CTC) into various components. 
                Understanding each component helps you calculate in-hand salary and plan your finances better. Basic 
                Salary is the core component (typically 40-60% of CTC) and forms the basis for other calculations like 
                HRA, PF, and gratuity. House Rent Allowance (HRA) is usually 40-50% of basic salary and provides tax 
                benefits if you live in rented accommodation. Special Allowance includes various allowances like transport, 
                medical, and other benefits - this is the balancing component. Provident Fund (PF) is a retirement benefit 
                where both employee (12% of basic) and employer (12% of basic) contribute monthly. Your PF contributions 
                are deducted from gross salary, reducing your in-hand salary. Gross Salary is the total before deductions, 
                while Net Salary (in-hand) is what you receive after PF and other deductions. CTC (Cost to Company) includes 
                gross salary plus employer's PF contribution, gratuity, and other benefits. Some components like performance 
                bonuses, retention bonuses, and joining bonuses may be paid annually. Understanding the difference between 
                CTC and in-hand salary is crucial when comparing job offers. A higher CTC doesn't always mean higher in-hand 
                salary - the breakup matters. Tax deductions (TDS) further reduce your in-hand salary based on your tax slab.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Salary Calculation Formulas:</h4>
            <div className="formula-display">
              Net Salary = Gross Salary - Employee PF - TDS
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Basic Salary</div>
                <div className="formula-param-value">Gross Salary × Basic %</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">HRA</div>
                <div className="formula-param-value">Basic Salary × HRA %</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Employee PF</div>
                <div className="formula-param-value">Basic Salary × 12%</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Special Allowance</div>
                <div className="formula-param-value">Gross - Basic - HRA - Other Allowances</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is the difference between CTC and in-hand salary?</h5>
              <p>
                CTC (Cost to Company) is the total amount a company spends on you including gross salary, employer PF, gratuity, and benefits. In-hand salary is what you actually receive after deductions like PF and TDS - typically 70-80% of CTC.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is PF calculated and can I withdraw it?</h5>
              <p>
                PF is 12% of basic salary deducted from your salary, with an equal employer contribution. You can withdraw PF when you're unemployed for 2+ months, retire, or for specific purposes like house purchase or medical emergency.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is HRA and how does it provide tax benefits?</h5>
              <p>
                HRA (House Rent Allowance) is typically 40-50% of basic salary. If you live in a rented house, you can claim tax exemption on HRA based on actual rent paid, 50% of basic (metro cities) or 40% (non-metros), or actual HRA received - whichever is lowest.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why is basic salary kept low in some companies?</h5>
              <p>
                Companies sometimes keep basic salary low (30-40% instead of 50%) to reduce PF contributions and gratuity liabilities, increasing the special allowance component. This gives you higher in-hand salary but lower retirement benefits.
              </p>
            </div>

            <div className="faq-item">
              <h5>What components should I look for when comparing job offers?</h5>
              <p>
                Look at: in-hand salary (not just CTC), basic salary percentage (higher is better for PF), fixed vs variable pay split, annual bonuses, health insurance coverage, leave policy, and other benefits like ESOPs or joining/retention bonuses.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is TDS calculated on salary?</h5>
              <p>
                TDS (Tax Deducted at Source) is based on your annual income and tax slab. Your employer deducts tax monthly based on your projected annual income. You can reduce TDS by declaring investments under 80C, HRA, home loan interest, and choosing the appropriate tax regime.
                </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryCalculator;